/**
 * Mock AuthApi implementation for development mode
 * Generates fake JWT tokens without backend validation
 */

import type { TokenPairDto } from "../../shared/contracts/model/tokenPairDto";
import type { FetcherConfig } from "../../shared/api/http";
import type { AuthApi } from "./auth-api";

/**
 * Simple JWT encoder (for dev purposes only)
 * Creates a basic JWT-like token structure
 */
function createMockJWT(payload: Record<string, any>): string {
  const header = {
    alg: "HS256",
    typ: "JWT",
  };
  
  // Set expiration to 1 hour from now
  const exp = Math.floor(Date.now() / 1000) + 3600;
  const fullPayload = {
    ...payload,
    exp,
    iat: Math.floor(Date.now() / 1000),
  };
  
  // Simple base64 encoding (not real JWT signing)
  const encodedHeader = btoa(JSON.stringify(header)).replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
  const encodedPayload = btoa(JSON.stringify(fullPayload)).replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
  const signature = "mock_signature";
  
  return `${encodedHeader}.${encodedPayload}.${signature}`;
}

/**
 * Parse initData to extract user info
 */
function parseInitData(initData: string): { telegramId?: number; username?: string } {
  try {
    const params = new URLSearchParams(initData);
    const userStr = params.get("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      return {
        telegramId: user.id,
        username: user.username,
      };
    }
  } catch (error) {
    console.warn("[MockAuthApi] Failed to parse initData", error);
  }
  return {};
}

export class MockAuthApi implements AuthApi {
  async telegramAuth(initData: string, config: FetcherConfig): Promise<TokenPairDto> {
    console.log("[MockAuthApi] telegramAuth() called with mock implementation");
    
    const { telegramId = 123456789, username } = parseInitData(initData);
    
    // Create mock tokens
    const accessToken = createMockJWT({
      sub: `user_${telegramId}`,
      telegramId,
      telegram_id: telegramId,
      username,
      type: "access",
    });
    
    const refreshToken = createMockJWT({
      sub: `user_${telegramId}`,
      telegramId,
      telegram_id: telegramId,
      type: "refresh",
    });
    
    return {
      accessToken,
      refreshToken,
    };
  }
  
  async refresh(refreshToken: string, config: FetcherConfig): Promise<TokenPairDto> {
    console.log("[MockAuthApi] refresh() called with mock implementation");
    
    // Decode refresh token to get user info
    try {
      const [, payloadPart] = refreshToken.split(".");
      if (payloadPart) {
        const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
        const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
        const json = atob(padded);
        const payload = JSON.parse(json);
        
        const telegramId = payload.telegramId || payload.telegram_id || 123456789;
        
        // Create new tokens
        const accessToken = createMockJWT({
          sub: payload.sub || `user_${telegramId}`,
          telegramId,
          telegram_id: telegramId,
          username: payload.username,
          type: "access",
        });
        
        const newRefreshToken = createMockJWT({
          sub: payload.sub || `user_${telegramId}`,
          telegramId,
          telegram_id: telegramId,
          type: "refresh",
        });
        
        return {
          accessToken,
          refreshToken: newRefreshToken,
        };
      }
    } catch (error) {
      console.warn("[MockAuthApi] Failed to decode refresh token", error);
    }
    
    // Fallback: create new tokens with default user
    return this.telegramAuth("", config);
  }
  
  async logout(config: FetcherConfig): Promise<void> {
    console.log("[MockAuthApi] logout() called with mock implementation");
    // No-op in mock mode
  }
}

